import { useState, useEffect, useCallback, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Box,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    useTheme,
    Tooltip,
    Autocomplete,
    TextField,
} from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PersonIcon from "@mui/icons-material/Person";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import CustomTextField from "components/Fileds/CustomTextFiled";
import CustomSelect from "components/Fileds/CustomSelect";
import CustomButton from "components/Fileds/CustomButton";
import CustomPhoneInput from "components/Fileds/CustomPhoneInput";
import { useAddUpdateCustomerMutation, useCheckAvailableTableQuery, useFindDeliveryFeeMutation, useGetAvailableTableQuery, useGetCustomerByPhoneMutation, useGetVendorTableQuery } from "pages/dashboard/services";
import { useGetSettingsQuery } from "pages/settings/ManageSettings/services";
import { useGetTableTimeSlotsQuery } from "pages/timeSlots/services";
import { convertTo24Hour, generateDays, getUTCDateTime, parseBookingDate, parseBookingTime, timeSlots, toMinutes } from "utils/function";
import { defaultTableOptions } from "utils/constant";
import AddIcon from '@mui/icons-material/Add';
import CustomTypography from "components/Fileds/CustomTypography";
import "./style.css";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useOrder } from "context/OrderContext";
import { focusFirstField } from "utils/focusHelper";

const googleLibraries = ['places'];

// Dialog types
const DIALOG_TYPES = {
    TAKE_AWAY: "takeAway",
    DINE_IN: "dineIn",
    TABLE_BOOKING: "tableBooking",
    PHONE_ORDER: "phoneOrder"
};

const baseShape = {
    phone_number: Yup.string().required("Phone number is required")
        .test(
            "min-digits",
            "Phone number must contain at least 10 digits",
            (value) => {
                if (!value) return false;
                // remove non-digit chars and check length
                const digitsOnly = String(value).replace(/\D/g, "");
                return digitsOnly.length >= 10;
            }
        ),
    customer_name: Yup.string().required("Customer name is required"),
};


// Initial values for different dialog types
function getInitialValues(dialogType, prefilledData = {}) {
    const baseValues = {
        id: prefilledData?.id || "",
        phone_number: prefilledData?.phone_number != null ? String(prefilledData.phone_number) : "",
        country_code: prefilledData?.country_code != null ? String(prefilledData.country_code) : "44",
        country_ISO: prefilledData?.country_ISO || "gb",
        customer_name: prefilledData?.customer_name != null ? String(prefilledData.customer_name) : "",
    };

    switch (dialogType) {
        case DIALOG_TYPES.TAKE_AWAY:
            return baseValues;
        case DIALOG_TYPES.DINE_IN:
            return {
                ...baseValues,
                persons: prefilledData?.persons || "1",
                table: prefilledData?.table || "",
            };
        case DIALOG_TYPES.TABLE_BOOKING:
            return {
                ...baseValues,
                persons: prefilledData?.persons || "1",
                table: prefilledData?.table || "",
                day: prefilledData?.day || generateDays()[0].value,
                time_slot: prefilledData?.time_slot || "",
                time: prefilledData?.time || "",
            };
        case DIALOG_TYPES.PHONE_ORDER:
            return {
                ...baseValues,
                customer_address: prefilledData?.customer_address || "",
                building_number: prefilledData?.building_number || "",
                building_name: prefilledData?.building_name || "",
            };
        default:
            return baseValues;
    }
}

function TableBookingFields({ categories, loadingSlots, slotsError, onAddTimeSlot }) {
    const theme = useTheme();
    const { values, setFieldValue, touched, errors } = useFormikContext();
    const days = generateDays();
    const today = new Date();

    const selectedDayObj = days.find((d) => d.value === values.day);
    const isToday =
        selectedDayObj != null &&
        selectedDayObj.date.toDateString() === today.toDateString();



    // 2. attach a key = minutes of first slot (or huge if none)
    // const catsWithKey = catsWithSortedSlots.map(cat => ({
    //     ...cat,
    //     firstSlotMinutes: cat.slots.length ? toMinutes(cat.slots[0]) : Number.MAX_SAFE_INTEGER,
    // }));

    // 3. sort categories by that key
    // const categories = catsWithKey
    //     .sort((a, b) => a.firstSlotMinutes - b.firstSlotMinutes)
    //     .map(({ firstSlotMinutes, ...cat }) => cat);

    // initialize time_slot + time on first load of categories
    // useEffect(() => {
    //     if (categories?.length > 0) {
    //         const first = categories[0];
    //         setFieldValue("time_slot", first.name);
    //         setFieldValue("time", first.slots[0] || "");
    //     }
    // }, []);

    useEffect(() => {
        if (categories.length === 0) return;

        // get all the valid category names
        const validSlots = categories.map(c => c.name);

        // if (categories.length > 0 && !values.time_slot) {
        //     const first = categories[0];
        //     setFieldValue("time_slot", first.name);
        //     setFieldValue("time", first.slots[0] || "");
        // }

        // if no slot selected, or selected slot no longer exists in this list
        if (!values.time_slot || !validSlots.includes(values.time_slot)) {
            const firstCat = categories[0];
            setFieldValue("time_slot", firstCat.name);
            setFieldValue("time", firstCat.slots[0]?.time || "");
        }
    }, [categories, values.time_slot, setFieldValue]);

    return (
        <>
            {/* Day Selection */}
            <Box sx={{ mb: '20px' }}>
                <CustomTypography label="Select Day" variant="body2" sx={{ mb: 0.5, fontWeight: 600, fontSize: "14px", color: theme.palette.typographyLabel.main }} />
                <ToggleButtonGroup
                    value={values.day}
                    exclusive
                    onChange={(_, newDay) => newDay && setFieldValue("day", newDay)}
                    aria-label="select day"
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        "& .MuiToggleButtonGroup-grouped": {
                            border: "1px solid rgba(161, 161, 161, 1)",
                            borderRadius: "10px !important",
                            m: 0.5,
                            height: "52px",
                            maxWidth: "100px",
                            flex: "1 0 auto",
                            fontSize: "16px",
                            padding: "9px",
                        },
                    }}
                >
                    {days?.map((day) => (
                        <ToggleButton key={day.value} value={day.value} disableRipple
                            sx={{
                                flexDirection: "column", p: 1, width: "100px", borderRadius: "10px !important", textTransform: "none",
                                backgroundColor: values.day === day.value ? theme.palette.primary.main : "transparent",
                                color: values.day === day.value ? theme.palette.secondary.main : theme.palette.drawermenuitem.main,
                                "&.Mui-selected": {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.secondary.main,
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.secondary.main,
                                },
                            }}
                        >
                            <CustomTypography label={day.label}
                                sx={{ fontSize: "16px", fontWeight: 700, lineHeight: '19px', mb: 0 }}
                            />
                            <CustomTypography label={day.subLabel}
                                sx={{ fontSize: "16px", lineHeight: '18px', mb: 0 }}
                            />
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Time Slot Category */}
            <Box sx={{ mb: '15px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CustomTypography label="Select Time Slot" variant="body2" sx={{ mb: 0, fontWeight: 600, fontSize: "14px", color: theme.palette.typographyLabel.main }} />
                    <Tooltip title="Add Time Slot" arrow placement="top">
                        <IconButton
                            size="small"
                            onClick={() => onAddTimeSlot && onAddTimeSlot(values)}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.common.white,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                width: 20,
                                height: 20,
                                ml: 1
                            }}
                        >
                            <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {loadingSlots ? (
                    <CircularProgress size="1.5rem" />
                ) : slotsError ? (
                    <Typography color="error">Failed to load slots</Typography>
                ) : (
                    <ToggleButtonGroup
                        value={values.time_slot}
                        exclusive
                        onChange={(_, newCat) => {
                            if (newCat) {
                                setFieldValue("time_slot", newCat);
                                setFieldValue("time", "");
                            }
                        }}
                        aria-label="select time slot"
                        sx={{
                            display: "flex",
                            "& .MuiToggleButtonGroup-grouped": {
                                // border: "1px solid rgba(0, 0, 0, 0.12)",
                                border: "1px solid rgba(161, 161, 161, 1)",
                                borderRadius: "10px !important",
                                m: 0.5,
                                padding: "4px 8px 4px 8px",
                                "&:hover": {
                                    backgroundColor: "transparent !important",
                                    color: "inherit !important",
                                },
                                lineHeight: '1.5'
                            },
                        }}
                    >
                        {categories.map((cat) => (
                            <ToggleButton key={cat.id} value={cat.name}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 'normal',
                                    backgroundColor: values.time_slot === cat ? theme.palette.primary.main : "transparent",
                                    color: values.time_slot === cat ? theme.palette.secondary.main : "inherit",
                                    paddingTop: '5px',
                                    "&.Mui-selected": {
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.secondary.main,
                                    },
                                    "&.Mui-selected:hover": {
                                        backgroundColor: `${theme.palette.primary.main} !important`,
                                        color: `${theme.palette.secondary.main} !important`,
                                    },
                                }}
                            >
                                {cat.name}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Box>

            {/* Actual Time Buttons */}
            {values.time_slot && (
                <Box sx={{ mb: '20px' }}>
                    <ToggleButtonGroup
                        value={values.time}
                        exclusive
                        onChange={(_, t) => t && setFieldValue("time", t)}
                        aria-label="select time"
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            backgroundColor: "#F5F5F5",
                            borderRadius: "10px",
                            padding: "10px",
                            "& .MuiToggleButtonGroup-grouped": {
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                borderRadius: "10px !important",
                                m: 0.5,
                                "&:hover": {
                                    backgroundColor: "transparent !important",
                                    color: "inherit !important",
                                },
                            },
                        }}
                    >
                        {categories
                            .find((c) => c.name === values.time_slot)
                            ?.slots.map((slot) => (
                                <ToggleButton key={slot.time} value={slot.time}
                                    disabled={slot.isBooked}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "20px",
                                        border: '1px solid #4D4D4D !important',
                                        padding: "2px 8px 2px 8px",
                                        fontWeight: 'normal',
                                        paddingTop: '4px',
                                        backgroundColor: values.time === slot.time ? "white" : "white",
                                        color: values.time === slot.time ? "#fff" : (slot.isBooked ? theme.palette.text.disabled : "inherit"),
                                        "&.Mui-selected": {
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.secondary.main,
                                        },
                                        "&.Mui-selected:hover": {
                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                            color: `${theme.palette.secondary.main} !important`,
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: '#E2E2E2',
                                            borderColor: '#E2E2E2 !important',
                                            color: theme.palette.text.disabled,
                                        }
                                    }}
                                >
                                    {convertTo24Hour(slot.time)}
                                </ToggleButton>
                            ))}
                    </ToggleButtonGroup>
                    {touched.time && errors.time && (
                        <Typography color="error" variant="body2" sx={{ mt: 0.5 }}>
                            {errors.time}
                        </Typography>
                    )}
                </Box >
            )
            }
        </>
    );
}


const OrderDialog = ({ open, onClose, dialogType, onSubmit, onAddTable, onAddTimeSlot }) => {
    const theme = useTheme();
    const vendorCountry = useSelector((state) => state?.posData?.country_code)
    const autofetchmobilenumbercount = useSelector((state) => state?.posData?.autofetchmobilenumbercount ?? 10);
    const { data: settingsResp } = useGetSettingsQuery();
    const mandatoryCustomerDetailsDineIn = settingsResp?.data?.settings?.mandatory_customer_details_dine_in !== undefined
        ? Boolean(settingsResp.data.settings.mandatory_customer_details_dine_in)
        : true;
    const [showCustomerFields, setShowCustomerFields] = useState(false);
    const isExpressDineIn = dialogType === DIALOG_TYPES.DINE_IN && !mandatoryCustomerDetailsDineIn;
    const areCustomerFieldsVisible = !isExpressDineIn || showCustomerFields;

    const [selectedTimeSlot, setSelectedTimeSlot] = useState("Breakfast");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryCode, setCountryCode] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");
    const [isFetchingCustomer, setIsFetchingCustomer] = useState(false);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [originalCustomerName, setOriginalCustomerName] = useState("");
    const [customerId, setCustomerId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const lastFetchedNumberRef = useRef("");
    const [checkArgs, setCheckArgs] = useState(null);
    const { orderType, orderItems, customerData, addItemToOrder, removeItemFromOrder, resetOrder, dialogPrefill } = useOrder();

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyDl33GnWibNPt7vrbnU1tBFNwQ4V98aChc",
        libraries: googleLibraries,
    });

    const {
        ready,
        value: placeValue,
        suggestions: { status: placesStatus, data: placesSuggestions },
        setValue: setPlaceValue,
        clearSuggestions,
        init: initPlaces,
    } = usePlacesAutocomplete({
        initOnMount: false,
        debounce: 300,
    });

    useEffect(() => {
        if (isLoaded) {
            initPlaces();
        }
    }, [isLoaded, initPlaces]);

    const posData = useSelector((state) => state?.posData);
    const vendorId = posData?.id;
    const [findDeliveryFeeApi] = useFindDeliveryFeeMutation();
    const [customerDeliveryFee, setCustomerDeliveryFee] = useState(0);
    const [deliveryFeeApiError, setDeliveryFeeApiError] = useState(false);
    const [deliveryFeeErrorMessage, setDeliveryFeeErrorMessage] = useState("");
    const [isFetchingDeliveryFee, setIsFetchingDeliveryFee] = useState(false);
    const [selectedGoogleAddress, setSelectedGoogleAddress] = useState(null);
    const [customerLat, setCustomerLat] = useState("");
    const [customerLong, setCustomerLong] = useState("");
    const [addressDetails, setAddressDetails] = useState(null);

    const handleSelectLocation = async (newValue, setFieldValue, setFieldError) => {
        console.log("Google Places Autocomplete Selected Location Raw:", newValue);

        // =========================================================
        // 1. User cleared the Google address
        // =========================================================
        if (!newValue) {
            setCustomerAddress("");
            setSelectedGoogleAddress(null);
            setCustomerLat("");
            setCustomerLong("");
            setCustomerDeliveryFee(0);
            setDeliveryFeeApiError(false);
            setDeliveryFeeErrorMessage("");
            setIsFetchingDeliveryFee(false);
            setAddressDetails(null);

            if (setFieldValue) {
                setFieldValue("customer_address", "");
                setFieldValue("lat", "");
                setFieldValue("long", "");
                setFieldValue("delivery_fee", 0);
                setFieldValue("city", "");
                setFieldValue("state", "");
                setFieldValue("postal_code", "");
            }

            if (setFieldError) {
                setFieldError("customer_address", undefined);
            }

            setPlaceValue("", false);
            clearSuggestions();

            return;
        }

        // =========================================================
        // 2. Get selected Google address
        // =========================================================
        const val = typeof newValue === "string" ? newValue : newValue.description;
        setCustomerAddress(val);

        if (setFieldValue) {
            setFieldValue("customer_address", val);
        }

        setPlaceValue(val, false);
        clearSuggestions();

        // =========================================================
        // 3. Start with delivery fee request state
        // =========================================================
        setDeliveryFeeApiError(false);
        setDeliveryFeeErrorMessage("");
        if (setFieldError) {
            setFieldError("customer_address", undefined);
        }
        setIsFetchingDeliveryFee(true);

        try {
            // =====================================================
            // 4. Get coordinates from Google
            // =====================================================
            const results = await getGeocode({ address: val, });

            if (!results || results.length === 0) {
                console.error("No Google geocode result found for address:", val);

                // Invalid Google address
                setSelectedGoogleAddress(null);
                setCustomerLat("");
                setCustomerLong("");
                setCustomerDeliveryFee(0);
                setAddressDetails(null);
                setDeliveryFeeApiError(true);

                if (setFieldValue) {
                    setFieldValue("lat", "");
                    setFieldValue("long", "");
                    setFieldValue("delivery_fee", 0);
                    setFieldValue("city", "");
                    setFieldValue("state", "");
                    setFieldValue("postal_code", "");
                }

                return;
            }

            const { lat, lng } = await getLatLng(results[0]);

            console.log("Google Places Full Geocode Result:", results[0]);
            console.log("Google Places Address Components:", results[0].address_components);
            console.log("Google Places Coordinates:", { lat, lng });

            const latStr = lat.toString();
            const lngStr = lng.toString();

            // =====================================================
            // 5. Extract address details
            // =====================================================
            let street_number = "";
            let route = "";
            let city = "";
            let state = "";
            let countryName = "";
            let postal_code = "";

            results[0].address_components.forEach((component) => {
                const types = component.types;
                if (types.includes("street_number")) street_number = component.long_name;
                if (types.includes("route")) route = component.long_name;
                if (types.includes("locality") || types.includes("postal_town")) city = component.long_name;
                if (types.includes("administrative_area_level_1")) state = component.long_name;
                if (types.includes("country")) countryName = component.long_name;
                if (types.includes("postal_code")) postal_code = component.long_name;
            });

            // =====================================================
            // 6. Call Delivery Fee API
            // =====================================================
            let calculatedDeliveryFee = 0;

            if (vendorId) {
                const feePayload = {
                    customer_lat: latStr,
                    customer_long: lngStr,
                    vendor_id: vendorId,
                };

                console.log("Calling Delivery Fee API Payload:", feePayload);

                try {
                    const feeRes = await findDeliveryFeeApi(feePayload).unwrap();

                    console.log("Delivery Fee API Response:", feeRes);

                    const responseStatus = feeRes?.status ?? feeRes?.data?.status;
                    const responseMessage = feeRes?.message || feeRes?.data?.message;

                    // Make sure API actually returned delivery_fee and no error status
                    if (
                        responseStatus === 422 ||
                        feeRes?.data?.delivery_fee === undefined ||
                        feeRes?.data?.delivery_fee === null
                    ) {
                        const errorMsg =
                            responseMessage ||
                            "Delivery is not available for your location. The restaurant has not configured delivery zones.";
                        console.error("Delivery Fee API returned error or no delivery_fee:", errorMsg);

                        setDeliveryFeeApiError(true);
                        setDeliveryFeeErrorMessage(errorMsg);
                        if (setFieldError) {
                            setFieldError("customer_address", errorMsg);
                        }

                        return;
                    }

                    calculatedDeliveryFee = parseFloat(feeRes.data.delivery_fee) || 0;

                    // API succeeded
                    setDeliveryFeeApiError(false);
                    setDeliveryFeeErrorMessage("");
                    if (setFieldError) {
                        setFieldError("customer_address", undefined);
                    }

                } catch (feeError) {
                    console.error("Error calling delivery fee API:", feeError);

                    // =================================================
                    // IMPORTANT:
                    // Delivery API failed, so this address is NOT valid
                    // =================================================
                    const errorMsg =
                        feeError?.data?.message ||
                        feeError?.message ||
                        feeError?.data?.error ||
                        "Delivery is not available for your location. The restaurant has not configured delivery zones.";

                    setDeliveryFeeApiError(true);
                    setDeliveryFeeErrorMessage(errorMsg);
                    if (setFieldError) {
                        setFieldError("customer_address", errorMsg);
                    }
                    return;
                }
            }

            // =====================================================
            // 7. Delivery fee API succeeded
            // =====================================================

            setCustomerLat(latStr);
            setCustomerLong(lngStr);
            setCustomerDeliveryFee(calculatedDeliveryFee);

            const fullExtractedLocation = {
                full_address: val,
                formatted_address: results[0].formatted_address || val,
                lat: latStr,
                long: lngStr,
                latitude: lat,
                longitude: lng,
                city,
                state,
                country: countryName,
                postal_code,
                delivery_fee: calculatedDeliveryFee,
                address_components: results[0].address_components,
            };

            console.log("Full Phone Order Location & Fee Data for DB Storage:", fullExtractedLocation);

            // =====================================================
            // IMPORTANT:
            // Only store this as the last valid Google address
            // AFTER delivery fee API succeeds.
            // =====================================================
            setSelectedGoogleAddress(fullExtractedLocation);
            setAddressDetails(fullExtractedLocation);

            // =====================================================
            // 8. Update Formik
            // =====================================================
            if (setFieldValue) {
                setFieldValue("customer_address", val);
                setFieldValue("lat", latStr);
                setFieldValue("long", lngStr);
                setFieldValue("delivery_fee", calculatedDeliveryFee);
                if (city) { setFieldValue("city", city); }
                if (state) { setFieldValue("state", state); }
                if (postal_code) { setFieldValue("postal_code", postal_code); }
            }

        } catch (error) {
            // =====================================================
            // 9. Google geocode failed
            // =====================================================
            console.error("Error fetching geocode for selected address:", error);

            setDeliveryFeeApiError(true);
            const geocodeError = "Unable to find location. Please select a valid address.";
            setDeliveryFeeErrorMessage(geocodeError);
            if (setFieldError) {
                setFieldError("customer_address", geocodeError);
            }
            setSelectedGoogleAddress(null);
            setCustomerLat("");
            setCustomerLong("");
            setCustomerDeliveryFee(0);
            setAddressDetails(null);
            if (setFieldValue) {
                setFieldValue("lat", "");
                setFieldValue("long", "");
                setFieldValue("delivery_fee", 0);
            }
        } finally {
            // =====================================================
            // 10. API / geocoding process finished
            // =====================================================
            setIsFetchingDeliveryFee(false);
        }
    };

    // const isPhoneOrderAddressValid = dialogType !== DIALOG_TYPES.PHONE_ORDER || Boolean(selectedGoogleAddress);
    const isPhoneOrderReady = dialogType !== DIALOG_TYPES.PHONE_ORDER || (
        Boolean(selectedGoogleAddress) &&
        !deliveryFeeApiError &&
        !deliveryFeeErrorMessage &&
        !isFetchingDeliveryFee &&
        Boolean(customerLat) &&
        Boolean(customerLong)
    );

    // Initialize API hooks
    const [getCustomerByPhone, { isLoading: isLoadingCustomer }] = useGetCustomerByPhoneMutation();
    const [addUpdateCustomer, { isLoading: isAddingCustomer }] = useAddUpdateCustomerMutation();
    const isDineInOpen = open && dialogType === DIALOG_TYPES.DINE_IN;

    // --- Time Slot Management ---
    const daysForSlots = generateDays();
    const [bookingDay, setBookingDay] = useState(daysForSlots[0].value);
    const [selectedTable, setSelectedTable] = useState("");
    const selectedDayObjForSlots = daysForSlots.find((d) => d.value === bookingDay);
    const isWeekendForSlots = selectedDayObjForSlots ? [0, 6].includes(selectedDayObjForSlots.date.getDay()) : false;

    const { data: rawCats = [], isLoading: loadingSlots, isError: slotsError, refetch: refetchTimeSlots } =
        useGetTableTimeSlotsQuery({
            is_weekend: isWeekendForSlots,
            table_id: selectedTable,
            booking_date: parseBookingDate(bookingDay),
            timezone_offset: new Date().getTimezoneOffset(),
        }, { skip: !bookingDay || dialogType !== DIALOG_TYPES.TABLE_BOOKING });

    // Processing categories (same logic as before, but here in OrderDialog)
    const today = new Date();
    const isTodayForSlots = selectedDayObjForSlots != null && selectedDayObjForSlots.date.toDateString() === today.toDateString();
    const nowMinutes = today.getHours() * 60 + today.getMinutes();

    const categories = rawCats.map(cat => ({
        ...cat,
        slots: [...cat.slots].sort((a, b) => toMinutes(a.time) - toMinutes(b.time)),
    }))
        .map(cat => ({
            ...cat,
            slots: isTodayForSlots ? cat.slots.filter(slot => toMinutes(slot.time) > nowMinutes) : cat.slots,
        }))
        .filter(cat => cat.slots.length > 0)
        .map(cat => ({
            ...cat,
            firstSlotMinutes: toMinutes(cat.slots[0].time),
        }))
        .sort((a, b) => a.firstSlotMinutes - b.firstSlotMinutes)
        .map(({ firstSlotMinutes, ...cat }) => cat);

    // Fetch tables from API
    const { data: tableData, isLoading: isLoadingTables, refetch: refetchTables } = useGetVendorTableQuery();
    // const {data: dineInTablesData, isLoading: loadingDineInTables, refetch: refetchDineInTables} = useGetAvailableTableQuery({ skip: !isDineInOpen });
    const { data: dineInTablesData, isLoading: loadingDineInTables, refetch: refetchDineInTables } = useGetAvailableTableQuery({ skip: !isDineInOpen });

    // for booking, we’ll call the “check” mutation on user‐selection:
    // const { data: checkData, isFetching: checkingSlot, error: checkError, refetch: refetchCheck } = useCheckAvailableTableQuery(checkArgs, { skip: !checkArgs, refetchOnMountOrArgChange: true, });

    // re‑fetch any time the modal opens
    useEffect(() => {
        if (open) {
            refetchTables();
        }
        if (isDineInOpen) {
            refetchDineInTables();
        }
    }, [open, isDineInOpen, refetchTables, refetchDineInTables, refetchTimeSlots]);

    // whenever the user picks a new date/slot/table, update checkArgs
    // const onBookingParamsChange = (newArgs) => {
    //     setCheckArgs(newArgs);
    //     // trigger the query immediately
    //     // refetchCheck();
    // };

    // whenever the user picks a new date/slot/table, update checkArgs
    const onBookingParamsChange = useCallback((newArgs) => {
        setCheckArgs(prev => {
            try {
                // avoid setting identical object (prevents rerender loops)
                if (JSON.stringify(prev) === JSON.stringify(newArgs)) return prev;
            } catch {
                // fallback if stringify fails for any reason
                console.error("Error in BookingParamsChanegs")
            }
            return newArgs;
        });
    }, [setCheckArgs]);


    // Build table options from API data
    const rawRecords = tableData?.data?.records || [];
    let tableOptions = defaultTableOptions
    // const tableOptions =
    //     rawRecords.length > 0
    //         ? rawRecords.map((table) => ({
    //             value: table?.id,
    //             label: table?.table_no ? `${table.table_no}` : `${table.id}`,
    //             capacity: table.capacity,
    //         }))
    //         : defaultTableOptions;
    const renderTableOptionLabel = (tableName, cap) => {
        if (cap === undefined || cap === null || cap === "") {
            return tableName;
        }
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
                <span>{tableName}</span>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        ml: 0.6,
                        color: 'inherit',
                    }}
                >
                    (<PersonIcon sx={{ fontSize: '15px', mx: '2px', verticalAlign: 'middle', color: 'inherit' }} />{cap})
                </Box>
            </Box>
        );
    };

    if (dialogType === DIALOG_TYPES.DINE_IN) {
        const recs = dineInTablesData?.data || [];
        tableOptions = recs.map(t => {
            const cap = t.capacity ?? t.totalCapacity;
            const tableName = t.merged_tables > 0 ? `${t.table_no} +(${t.merged_tables})` : t.table_no;
            return {
                value: t.id,
                table_no: t.table_no,
                label: renderTableOptionLabel(tableName, cap),
                capacity: t.capacity,
                totalCapacity: t.totalCapacity,
            };
        });
    } else if (dialogType === DIALOG_TYPES.TABLE_BOOKING) {
        const recs = tableData?.data?.records || [];
        tableOptions = recs.map(t => {
            const cap = t.totalCapacity ?? t.capacity;
            const tableName = t.mergedCount > 0 ? `${t.table_no} +(${t.mergedCount})` : t.table_no;
            return {
                value: t.id,
                table_no: t.table_no,
                label: renderTableOptionLabel(tableName, cap),
                capacity: t.capacity,
                totalCapacity: t.totalCapacity,
            };
        });
    }

    // 2. Ensure occupied table (from edit mode via dialogPrefill) is present
    if (
        dialogPrefill?.table?.id &&
        !tableOptions.find(o => o.value === dialogPrefill?.table.id)
    ) {
        const prefillCap = dialogPrefill?.table?.capacity ?? dialogPrefill?.table?.totalCapacity;
        const prefillName = dialogPrefill?.table?.table_no;
        tableOptions = [
            {
                value: dialogPrefill?.table?.id,
                table_no: prefillName,
                label: renderTableOptionLabel(prefillName, prefillCap),
                capacity: dialogPrefill?.table?.capacity || 0,
            },
            ...tableOptions,
        ];
    }

    // 3. Ensure currently assigned table (from customerData edit mode) is present
    if (
        isEditMode &&
        customerData?.table &&
        customerData?.tableObj &&
        !tableOptions.find(o => Number(o.value) === Number(customerData.table))
    ) {
        const objCap = customerData.tableObj.capacity ?? customerData.tableObj.totalCapacity;
        const objName = customerData.tableObj.table_no;
        tableOptions = [
            {
                value: customerData.tableObj.id,
                table_no: objName,
                label: renderTableOptionLabel(objName, objCap),
                capacity: customerData.tableObj.capacity || 0,
            },
            ...tableOptions,
        ];
    }

    const validationSchemas = {
        [DIALOG_TYPES.TAKE_AWAY]: Yup.object().shape(baseShape),
        [DIALOG_TYPES.DINE_IN]: Yup.object().shape({
            ...baseShape,
            // persons: Yup.number().required("Number of persons is required")
            //     .min(1, "At least 1 person required"),
            // table: Yup.string().required("Table selection is required"),
            persons: Yup.number()
                .typeError("Must be a number")
                .integer("Must be an integer")
                .min(1, "At least one person required")
                .test(
                    "max-capacity",
                    "Table capacity exceeded!",
                    function (persons) {
                        const rawTable = this.parent.table;
                        if (!rawTable) return true;                // no table selected → skip
                        const tableId = Number(rawTable);          // coerce from string if needed
                        const opt = tableOptions.find(o => o.value === tableId);
                        if (!opt) return true;                     // unknown table → skip
                        // Use totalCapacity if present, otherwise fall back to capacity
                        const capacity = opt.totalCapacity ?? opt.capacity;
                        if (!capacity) return true; // skip if capacity is missing
                        return persons <= capacity;            // enforce capacity
                    }
                ),
            table: Yup.number().required("Table selection is required"),
        }),
        [DIALOG_TYPES.TABLE_BOOKING]: Yup.object().shape({
            ...baseShape,
            // persons: Yup.number().required("Number of persons is required").min(1, "At least 1 person required"),
            persons: Yup.number()
                .typeError("Must be a number")
                .integer("Must be an integer")
                .min(1, "At least one person required")
                .test(
                    "max-capacity",
                    "Table capacity exceeded!",
                    function (persons) {
                        const rawTable = this.parent.table;
                        if (!rawTable) return true;                // no table selected → skip
                        const tableId = Number(rawTable);          // coerce from string if needed
                        const opt = tableOptions.find(o => o.value === tableId);
                        if (!opt) return true;                     // unknown table → skip
                        const capacity = opt.totalCapacity ?? opt.capacity;
                        if (!capacity) return true; // skip if capacity is missing
                        return persons <= capacity;                 // enforce capacity
                    }
                ),
            table: Yup.number().required("Table selection is required"),
            // table: Yup.string().required("Table selection is required"),
            day: Yup.string().required("Day selection is required"),
            time_slot: Yup.string().required("Time slot is required"),
            time: Yup.string().required("Time is required"),
        }),
        [DIALOG_TYPES.PHONE_ORDER]: Yup.object().shape({
            ...baseShape,
            customer_address: Yup.string().required("Customer address is required"),
        }),
    };

    const getValidationSchema = (type) => {
        if (type === DIALOG_TYPES.DINE_IN && !mandatoryCustomerDetailsDineIn) {
            return Yup.object().shape({
                phone_number: Yup.string()
                    .nullable()
                    .notRequired()
                    .test(
                        "min-digits",
                        "Phone number must contain at least 10 digits",
                        (value) => {
                            if (!value) return true;
                            const digitsOnly = String(value).replace(/\D/g, "");
                            return digitsOnly.length >= 10;
                        }
                    ),
                customer_name: Yup.string().nullable().notRequired(),
                persons: Yup.number()
                    .typeError("Must be a number")
                    .integer("Must be an integer")
                    .min(1, "At least one person required")
                    .test(
                        "max-capacity",
                        "Table capacity exceeded!",
                        function (persons) {
                            const rawTable = this.parent.table;
                            if (!rawTable) return true;
                            const tableId = Number(rawTable);
                            const opt = tableOptions.find(o => o.value === tableId);
                            if (!opt) return true;
                            const capacity = opt.totalCapacity ?? opt.capacity;
                            if (!capacity) return true;
                            return persons <= capacity;
                        }
                    ),
                table: Yup.number().required("Table selection is required"),
            });
        }
        return validationSchemas[type];
    };

    // Check if dialog was opened via edit button
    useEffect(() => {
        if (open) {
            // 1. Check for Edit mode (from Context customerData)
            const hasCustomerData = Boolean(customerData && Object.keys(customerData).length > 0);
            setIsEditMode(hasCustomerData);

            if (hasCustomerData) {
                // Populate from editing data
                setPhoneNumber(customerData.phone_number != null ? String(customerData.phone_number) : "");
                setCountryCode(customerData.country_code != null ? String(customerData.country_code) : vendorCountry);
                setCustomerName(customerData.customer_name != null ? String(customerData.customer_name) : "");
                const addr = customerData.customer_address || "";
                setCustomerAddress(addr);
                setPlaceValue(addr, false);
                setOriginalCustomerName(customerData.customer_name != null ? String(customerData.customer_name) : "");
                setCustomerId(customerData.id || null);

                if (customerData.time_slot) {
                    setSelectedTimeSlot(customerData.time_slot);
                }

                if (dialogType === DIALOG_TYPES.DINE_IN && !mandatoryCustomerDetailsDineIn) {
                    if (customerData.customer_name || customerData.phone_number) {
                        setShowCustomerFields(true);
                    } else {
                        setShowCustomerFields(false);
                    }
                }
            }
            // 2. Check for Draft mode (from dialogPrefill, preserved when switching modals)
            else if (dialogPrefill && Object.keys(dialogPrefill).length > 0) {
                // Restore from draft
                setPhoneNumber(dialogPrefill.phone_number != null ? String(dialogPrefill.phone_number) : "");
                setCountryCode(dialogPrefill.country_code != null ? String(dialogPrefill.country_code) : vendorCountry);
                setCustomerName(dialogPrefill.customer_name != null ? String(dialogPrefill.customer_name) : "");
                const addr = dialogPrefill.customer_address || "";
                setCustomerAddress(addr);
                setPlaceValue(addr, false);
                setOriginalCustomerName(dialogPrefill.customer_name != null ? String(dialogPrefill.customer_name) : "");
                setCustomerId(dialogPrefill.id || null);

                if (dialogPrefill.time_slot) {
                    setSelectedTimeSlot(dialogPrefill.time_slot);
                }

                if (dialogType === DIALOG_TYPES.DINE_IN && !mandatoryCustomerDetailsDineIn) {
                    if (dialogPrefill.showCustomerFields || dialogPrefill.customer_name || dialogPrefill.phone_number) {
                        setShowCustomerFields(true);
                    } else {
                        setShowCustomerFields(false);
                    }
                }
            }
            else {
                // 3. Reset fields for a fresh order
                setPhoneNumber("");
                setCountryCode(vendorCountry);
                setCustomerName("");
                setCustomerAddress("");
                setPlaceValue("", false);
                setOriginalCustomerName("");
                setCustomerId(null);
                setSelectedTimeSlot("Breakfast");
                setIsNewCustomer(false);
                lastFetchedNumberRef.current = "";
                setShowCustomerFields(false);
            }
        }
    }, [open, customerData, vendorCountry, dialogPrefill, dialogType, mandatoryCustomerDetailsDineIn]);

    // Update bookingDay when Formik day changes
    const DaySlotSync = () => {
        const { values } = useFormikContext();
        useEffect(() => {
            if (values.day) {
                setBookingDay(values.day);
            }
            if (values.table) {
                setSelectedTable(values.table);
            }
        }, [values.day, values.table]);
        return null;
    };

    function BookingParamsWatcher({
        dialogType,
        onBookingParamsChange,
        categories
    }) {
        const { values } = useFormikContext();

        useEffect(() => {
            if (
                dialogType === DIALOG_TYPES.TABLE_BOOKING &&
                values.day &&
                values.time_slot &&
                values.time &&
                values.table
            ) {
                const { utcDate, utcTime } = getUTCDateTime(values.day, values.time);

                // Calculate end_time logic (same as submit)
                let end_time_utc = "";

                if (categories && categories.length > 0) {
                    const startMinutes = toMinutes(values.time);

                    // Flatten all slots for calculation
                    const allSlotsFlat = categories
                        .flatMap(cat => cat.slots)
                        .map(slot => ({ original: slot.time, minutes: toMinutes(slot.time) }))
                        .sort((a, b) => a.minutes - b.minutes);

                    const nextSlot = allSlotsFlat.find(s => s.minutes > startMinutes);
                    let localEndTime = "";
                    if (nextSlot && nextSlot.minutes <= startMinutes + 90) {
                        localEndTime = parseBookingTime(nextSlot.original);
                    } else {
                        const endMinutes = startMinutes + 60;
                        const h = Math.floor(endMinutes / 60);
                        const m = endMinutes % 60;
                        const hh = h.toString().padStart(2, "0");
                        const mm = m.toString().padStart(2, "0");
                        localEndTime = `${hh}:${mm}:00`;
                    }

                    if (localEndTime) {
                        // Manual UTC conversion because localEndTime is already in 24h format (HH:MM:SS)
                        // and getUTCDateTime expects AM/PM format.
                        const datePart = parseBookingDate(values.day);
                        const [Y, M, D] = datePart.split('-').map(Number);
                        const [hh, mm, ss] = localEndTime.split(':').map(Number);

                        const localDT = new Date(Y, M - 1, D, hh, mm, ss || 0);
                        const iso = localDT.toISOString();
                        const [utcDate, utcTimeWithMs] = iso.split('T');
                        end_time_utc = utcTimeWithMs.split('.')[0];
                    }
                }

                onBookingParamsChange({
                    booking_date: utcDate,
                    booking_time: utcTime,    // e.g. "12:55:00"
                    table_id: Number(values.table),
                    end_time: end_time_utc
                });
                console.log(end_time_utc, "end_time_utc==>>>")
            }
        }, [
            dialogType,
            values.day,
            values.time_slot,
            values.time,
            values.table,
            onBookingParamsChange,
            categories
        ]);

        return null; // this component doesn't render anything
    }

    const handlePhoneChange = async (value, country, formikHelpers) => {
        setPhoneNumber(value);

        // Update country code if provided
        // if (country?.dialCode) {
        //     setCountryCode(country.dialCode.replace("+", ""));
        // }

        // If we have a complete phone number, fetch customer data
        // Use strict equality for the length and check if we already fetched this number
        if (value && value.length === autofetchmobilenumbercount && value !== lastFetchedNumberRef.current) {
            setIsFetchingCustomer(true);
            lastFetchedNumberRef.current = value;

            try {
                // Call the API to search for customer by phone
                const response = await getCustomerByPhone({
                    country_code: countryCode,
                    phone: value,
                });

                // Check if we got customer data back
                if (response.data && (response.data?.data?.customer || response.data?.data?.customerVendor)) {
                    const customerData = response?.data;
                    handleCustomerDataFetch(customerData, formikHelpers);
                    setIsNewCustomer(false);
                } else {
                    // No customer found
                    setCustomerName("");
                    setOriginalCustomerName("");
                    setCustomerId(null);
                    if (formikHelpers) {
                        formikHelpers.setFieldValue("customer_name", "");
                        formikHelpers.setFieldValue("id", null);
                    }
                    setIsNewCustomer(true);
                }
            } catch (error) {
                console.error("Error fetching customer:", error);
                setCustomerName("");
                setOriginalCustomerName("");
                setCustomerId(null);
                if (formikHelpers) {
                    formikHelpers.setFieldValue("customer_name", "");
                    formikHelpers.setFieldValue("id", null);
                }
                setIsNewCustomer(true);
            } finally {
                setIsFetchingCustomer(false);
            }
        }
    };

    function handleCustomerDataFetch(customerData, formikHelpers) {
        if (customerData?.data?.customer || customerData?.data?.customerVendor) {
            const cust = customerData?.data?.customer || customerData?.data?.customerVendor;
            const name = cust?.customer_full_name ? cust?.customer_full_name : cust?.full_name || "";
            const address = cust?.customer_address || cust?.address || "";
            const id = cust?.id || customerData?.data?.customerVendor?.id || customerData?.data?.customer?.id || null;
            setCustomerName(name);
            setOriginalCustomerName(name);
            setCustomerId(id);

            if (address) {
                setCustomerAddress(address);
                setPlaceValue(address, false);
            }

            // Update Formik values atomically in one dispatch to prevent race conditions on isValid
            if (formikHelpers?.setValues) {
                formikHelpers.setValues((prev) => ({
                    ...prev,
                    customer_name: name,
                    ...(address ? { customer_address: address } : {}),
                    ...(id ? { id: id } : {}),
                }));
            } else if (formikHelpers) {
                if (name) formikHelpers.setFieldValue("customer_name", name);
                if (address) formikHelpers.setFieldValue("customer_address", address);
                if (id) formikHelpers.setFieldValue("id", id);
            }

            if (formikHelpers?.validateForm) {
                setTimeout(() => {
                    formikHelpers.validateForm();
                }, 0);
            }
        } else {
            setCustomerName("");
            setOriginalCustomerName("");
            setCustomerId(null);
            if (formikHelpers?.setValues) {
                formikHelpers.setValues((prev) => ({
                    ...prev,
                    customer_name: "",
                    id: null,
                }));
            } else if (formikHelpers) {
                formikHelpers.setFieldValue("customer_name", "");
                formikHelpers.setFieldValue("id", null);
            }
        }
    }

    const days = generateDays();

    // Helper to extract the full form draft including React state
    const getCurrentDraft = (currentValues) => ({
        ...currentValues,
        id: customerId || currentValues?.id || null,
        phone_number: phoneNumber != null ? String(phoneNumber) : (currentValues?.phone_number != null ? String(currentValues.phone_number) : ""),
        customer_name: customerName != null ? String(customerName) : (currentValues?.customer_name != null ? String(currentValues.customer_name) : ""),
        country_code: countryCode != null ? String(countryCode) : (currentValues?.country_code != null ? String(currentValues.country_code) : vendorCountry),
        customer_address: customerAddress || currentValues?.customer_address || "",
        lat: customerLat || currentValues?.lat || "",
        long: customerLong || currentValues?.long || "",
        delivery_fee: customerDeliveryFee || currentValues?.delivery_fee || 0,
        address_details: addressDetails || currentValues?.address_details || null,
        persons: currentValues?.persons,
        table: currentValues?.table,
        day: currentValues?.day,
        time_slot: currentValues?.time_slot,
        time: currentValues?.time,
        showCustomerFields: areCustomerFieldsVisible,
    });

    // Get dialog title based on type
    const getDialogTitle = () => {
        switch (dialogType) {
            case DIALOG_TYPES.TAKE_AWAY:
                return "Creating Take Away Order";
            case DIALOG_TYPES.DINE_IN:
                return "Creating Dine-in Order";
            case DIALOG_TYPES.TABLE_BOOKING:
                return "Booking a Table";
            case DIALOG_TYPES.PHONE_ORDER:
                return "Phone Order";
            default:
                return "Create Order";
        }
    };

    // Handle form submission
    const handleFormSubmit = async (values, { setSubmitting }) => {
        // 1) Only for table bookings on “Today”
        if (dialogType === DIALOG_TYPES.TABLE_BOOKING) {
            const days = generateDays();
            const sel = days.find(d => d.value === values.day);
            const selectedDate = sel?.date;
            const today = new Date();

            if (
                selectedDate &&
                selectedDate.toDateString() === today.toDateString() &&
                values.time
            ) {
                // —— parse by slicing, no regex ——
                const raw = values.time;              // e.g. "04:26PM"
                const ampm = raw.slice(-2).toUpperCase();      // "PM"
                const [hourStr, minuteStr] = raw.slice(0, -2).split(":");  // ["04","26"]
                let hh = parseInt(hourStr, 10);
                const mm = parseInt(minuteStr, 10);

                // convert to 24h
                if (ampm === "PM" && hh !== 12) hh += 12;
                if (ampm === "AM" && hh === 12) hh = 0;

                // build a Date at today’s Y/M/D/hh/mm
                const slotDateTime = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                    hh,
                    mm
                );

                // if slot is ≤ now, error out
                if (slotDateTime.getTime() <= Date.now()) {
                    toast.error("That time slot has already passed. Please pick a future time.")
                    setSubmitting(false);
                    return;
                }
            }
        }

        try {
            // Check if we need to add/update customer
            let finalCustomerId = customerId || values?.id || dialogPrefill?.id || null;
            const hasCustomerInfo = Boolean(
                String(customerName || "").trim() || String(phoneNumber || "").trim()
            );

            if (hasCustomerInfo) {
                // If ID is not resolved yet, but we have a valid phone number, check if customer already exists in backend
                if (!finalCustomerId && phoneNumber && phoneNumber.length >= (autofetchmobilenumbercount || 10)) {
                    try {
                        const phoneRes = await getCustomerByPhone({
                            country_code: countryCode,
                            phone: phoneNumber,
                        });
                        const existingCust = phoneRes?.data?.data?.customer || phoneRes?.data?.data?.customerVendor;
                        const existingId = existingCust?.id || phoneRes?.data?.data?.customerVendor?.id || phoneRes?.data?.data?.customer?.id;
                        if (existingId) {
                            finalCustomerId = existingId;
                            setCustomerId(existingId);
                            const existingName = existingCust?.customer_full_name || existingCust?.full_name || "";
                            if (!originalCustomerName) {
                                setOriginalCustomerName(existingName);
                            }
                        }
                    } catch (err) {
                        console.error("Error verifying customer by phone on submit:", err);
                    }
                }

                const isNewCustomer2 = !finalCustomerId;
                const didNameChange = Boolean(
                    originalCustomerName &&
                    customerName &&
                    String(customerName).trim() !== String(originalCustomerName).trim()
                );

                // If it's a new customer OR name was edited, call add/update API
                if (isNewCustomer2 || didNameChange) {
                    const customerPayload = {
                        country_code: countryCode?.toString().startsWith("+") ? countryCode : `+${countryCode}`,
                        phone: phoneNumber,
                        is_new: isNewCustomer2,
                        full_name: customerName,
                    };

                    const response = await addUpdateCustomer({ body: customerPayload });
                    const newId = response?.data?.data?.customerVendor?.id
                               || response?.data?.data?.customer?.id
                               || response?.data?.data?.id
                               || response?.data?.id;

                    if (newId) {
                        setCustomerId(newId);
                        finalCustomerId = newId;
                    }
                }
            }

            // Build tableObj (full object) from selected table id so we can pass it in submission
            let tableObj = null;
            if (values.table) {
                const tableId = Number(values.table);
                const opt = tableOptions.find(o => Number(o.value) === tableId);
                if (opt) {
                    tableObj = {
                        id: Number(opt.value),
                        table_no: opt.table_no || opt.label,
                        capacity: opt.totalCapacity ?? opt.capacity ?? 0,
                        raw: opt,
                    };
                } else if (dialogPrefill?.table && Number(dialogPrefill.table.id) === tableId) {
                    tableObj = {
                        id: Number(dialogPrefill.table.id),
                        table_no: dialogPrefill.table.table_no,
                        capacity: dialogPrefill.table.capacity ?? 0,
                        raw: dialogPrefill.table,
                    };
                }
            }

            // Calculate end_time for table bookings
            let end_time = "";
            if (dialogType === DIALOG_TYPES.TABLE_BOOKING && values.time) {
                const startMinutes = toMinutes(values.time);

                // Flatten all slots for calculation
                const allSlotsFlat = categories
                    .flatMap(cat => cat.slots)
                    .map(slot => ({ original: slot.time, minutes: toMinutes(slot.time) }))
                    .sort((a, b) => a.minutes - b.minutes);

                const nextSlot = allSlotsFlat.find(s => s.minutes > startMinutes);

                if (nextSlot && nextSlot.minutes <= startMinutes + 90) {
                    end_time = parseBookingTime(nextSlot.original);
                } else {
                    const endMinutes = startMinutes + 60;
                    const h = Math.floor(endMinutes / 60);
                    const m = endMinutes % 60;
                    const hh = h.toString().padStart(2, "0");
                    const mm = m.toString().padStart(2, "0");
                    end_time = `${hh}:${mm}:00`;
                }
            }

            // Convert calculated local end_time to UTC
            let end_time_utc = "";
            if (end_time) {
                const datePart = parseBookingDate(values.day);
                const [Y, M, D] = datePart.split('-').map(Number);
                const [hh, mm, ss] = end_time.split(':').map(Number);

                const localDT = new Date(Y, M - 1, D, hh, mm, ss || 0);
                const iso = localDT.toISOString();
                const [utcDate, utcTimeWithMs] = iso.split('T');
                end_time_utc = utcTimeWithMs.split('.')[0];
            }

            let finalAddress = customerAddress;
            let finalLat = customerLat;
            let finalLong = customerLong;
            let finalDeliveryFee = customerDeliveryFee;
            let finalAddressDetails = addressDetails;

            if (dialogType === DIALOG_TYPES.PHONE_ORDER) {
                if (!selectedGoogleAddress) {
                    toast.error("Please select an address from Google suggestions.");
                    setSubmitting(false);
                    return;
                }

                // Use the user's edited address text if modified, otherwise fallback to Google's address text
                finalAddress = customerAddress || values.customer_address || selectedGoogleAddress.full_address;
                finalLat = selectedGoogleAddress.lat;
                finalLong = selectedGoogleAddress.long;
                finalDeliveryFee = selectedGoogleAddress.delivery_fee;
                finalAddressDetails = addressDetails ? { ...addressDetails, full_address: finalAddress } : { ...selectedGoogleAddress, full_address: finalAddress };
            }

            // Now always forward the final values
            const updatedValues = {
                ...values,
                id: finalCustomerId,
                phone_number: phoneNumber,
                country_code: countryCode,
                customer_name: customerName,
                customer_address: finalAddress,
                lat: finalLat || values?.lat || "",
                long: finalLong || values?.long || "",
                latitude: finalLat || values?.lat || "",
                longitude: finalLong || values?.long || "",
                delivery_fee: finalDeliveryFee || customerDeliveryFee || values?.delivery_fee || 0,
                address_details: finalAddressDetails,
                // attach the whole selected table object as requested
                tableObj,
                end_time: end_time_utc, // Pass the UTC converted end_time
            };
            console.log("Submitting Order Customer & Location Values:", updatedValues);
            onSubmit(updatedValues);
        } catch (error) {
            console.error("Error submitting form:", error);
        } finally {
            setSubmitting(false);
        }
    };


    const handleCloseCancel = () => {
        setPhoneNumber("");
        setCountryCode(vendorCountry);
        setCustomerName("");
        setCustomerAddress("");
        setPlaceValue("", false);
        setCustomerLat("");
        setCustomerLong("");
        setCustomerDeliveryFee(0);
        setAddressDetails(null);
        setSelectedGoogleAddress(null);
        setDeliveryFeeApiError(false);
        setDeliveryFeeErrorMessage("");
        setIsFetchingDeliveryFee(false);
        setOriginalCustomerName("");
        setCustomerId(null);
        setIsEditMode(false);
        setSelectedTimeSlot("Breakfast");
        setIsNewCustomer(false);
        lastFetchedNumberRef.current = "";
        setShowCustomerFields(false);
        onClose();
    }

    // const effectivePhone = phoneNumber;
    const effectivePhone = phoneNumber;


    if (!dialogType) return null;
    return (
        <Dialog
            open={open}
            onClose={handleCloseCancel}
            maxWidth="sm"
            className="order-items-form-dialog"
            PaperProps={{
                sx: {
                    width: "450px",
                    padding: "20px 0px 30px 0px",
                    borderRadius: "20px",
                    backgroundColor: "white",
                },
            }}
            TransitionProps={{
                onEntered: (node) => {
                    focusFirstField(node);
                },
            }}
        >
            <IconButton aria-label="close" onClick={handleCloseCancel} className="close-icon">
                <CloseIcon />
            </IconButton>
            <DialogTitle className='text-align-center' sx={{ pb: 0 }}>
                <CustomTypography variant="h5" component={"div"}
                    label={getDialogTitle()}
                    sx={{
                        fontSize: '24px', fontWeight: 700,
                        color: theme.palette.typographyLabel.main, mb: 0
                    }}
                />
                {areCustomerFieldsVisible && (
                    <CustomTypography
                        label={"For existing customer, details will be auto fetched using mobile number"}
                        sx={{ mt: 0.5, fontSize: '12px', fontWeight: 400, color: '#111111', mb: 0 }}
                    />
                )}
            </DialogTitle>

            <DialogContent sx={{
                mx: 3, padding: "0px", scrollbarWidth: "none", msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                    display: "none",
                }
            }}
            >
                <Formik
                    enableReinitialize={true}
                    initialValues={getInitialValues(
                        dialogType,
                        // isEditMode ? customerData : {table: dialogPrefill?.table?.id} || {}
                        {
                            ...(isEditMode ? customerData : {}),
                            ...dialogPrefill,
                            //  // Ensure table ID is used for Formik's table field
                            // table: (typeof dialogPrefill?.table === 'object' ? dialogPrefill?.table?.id : dialogPrefill?.table) || ""

                            // Only extract table ID from dialogPrefill when it exists (as a full object).
                            // If dialogPrefill.table is absent, the table value from customerData (spread above) is preserved.
                            ...(dialogPrefill?.table != null && {
                                table: typeof dialogPrefill.table === 'object'
                                    ? (dialogPrefill.table.id || "")
                                    : (dialogPrefill.table || "")
                            })
                        }
                    )}
                    validationSchema={getValidationSchema(dialogType)}
                    onSubmit={handleFormSubmit}
                    // validateOnChange={false}
                    validateOnBlur={true}
                    validateOnMount={true}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, setValues, isSubmitting, setFieldError, validateForm, isValid }) => {
                        // const isTableUnavailable =
                        //     dialogType === DIALOG_TYPES.TABLE_BOOKING &&
                        //     checkError?.data?.status === 409;

                        return (
                            <Form className="order-form-container" onSubmit={handleSubmit} data-formik-form>
                                <DaySlotSync />
                                <BookingParamsWatcher
                                    dialogType={dialogType}
                                    onBookingParamsChange={onBookingParamsChange}
                                    categories={categories}
                                />
                                <Box className='order-dialog-form'>
                                    {areCustomerFieldsVisible && (
                                        <>
                                            {/* Customer Mobile Number */}
                                            <Box sx={{ mb: '20px' }}>
                                                <CustomTypography
                                                    label={"Customer Mobile Number"}
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                        color: theme.palette.typographyLabel.main
                                                    }}
                                                />
                                                <CustomPhoneInput
                                                    width="400px"
                                                    value={effectivePhone}
                                                    onChange={(value, country) => {
                                                        handlePhoneChange(value, country, { setFieldValue, setFieldTouched, setValues, validateForm });
                                                        setFieldValue("phone_number", value);
                                                        // if (country?.dialCode) {
                                                        setFieldValue(
                                                            "country_code",
                                                            vendorCountry);
                                                        // }
                                                        if (country?.iso2) {
                                                            setFieldValue("country_ISO", country.iso2);
                                                        }
                                                    }}
                                                    isLoading={isLoadingCustomer}
                                                />
                                                {touched.phone_number && errors.phone_number && (
                                                    <Typography
                                                        variant="body2"
                                                        color="error"
                                                        sx={{ marginTop: "4px", fontSize: "12px" }}
                                                    >
                                                        {errors.phone_number}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Customer Name */}
                                            <Box sx={{ mb: '20px' }}>
                                                <CustomTypography
                                                    label={"Customer Name"}
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                        color: theme.palette.typographyLabel.main
                                                    }}
                                                />
                                                {isFetchingCustomer || isLoadingCustomer ? (
                                                    <Box className='fetching-customer-container'>
                                                        <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                            Fetching details...
                                                        </Typography>
                                                        <CircularProgress
                                                            size="1rem"
                                                            sx={{ position: "absolute", right: "12px", color: "inherit", }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <CustomTextField
                                                        name="customer_name"
                                                        value={customerName}
                                                        sx={{
                                                            width: "400px",
                                                            height: '45px'
                                                        }}
                                                        onChange={(e) => {
                                                            const newValue = e.target.value;
                                                            setCustomerName(newValue);
                                                            setFieldValue("customer_name", newValue);
                                                        }}
                                                        onBlur={(e) => {
                                                            handleBlur(e);
                                                            // Only validate on blur
                                                            setFieldTouched("customer_name", true);
                                                        }}
                                                        error={
                                                            touched.customer_name && Boolean(errors.customer_name)
                                                        }
                                                        helperText={touched.customer_name && errors.customer_name}
                                                        placeholder="Customer Full Name"
                                                    />
                                                )}
                                            </Box>
                                        </>
                                    )}

                                    {/* Additional fields for Dine-In and Table Booking */}
                                    {(dialogType === DIALOG_TYPES.DINE_IN ||
                                        dialogType === DIALOG_TYPES.TABLE_BOOKING) && (
                                            <Grid container sx={{ mb: '20px' }}>
                                                {/* Number of Persons */}
                                                <Grid item xs={5} gap={0.5}>
                                                    <CustomTypography
                                                        label={"No. of Person(s)"}
                                                        variant="body2"
                                                        sx={{
                                                            mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                            color: theme.palette.typographyLabel.main
                                                        }}
                                                    />
                                                    <CustomTextField
                                                        name="persons"
                                                        type="number"
                                                        value={values.persons}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;

                                                            // 1) Always allow clearing the field
                                                            if (raw === "") {
                                                                handleChange(e);
                                                                // keep Formik’s value in sync
                                                                setFieldValue("persons", "");
                                                                // clear table whenever they blank it out
                                                                // setFieldValue("table", "");
                                                                // setFieldError("table", undefined);
                                                                if (!dialogPrefill?.table) {
                                                                    setFieldValue("table", "");
                                                                    setFieldError("table", undefined);
                                                                }
                                                                return;
                                                            }

                                                            const num = Number(raw);

                                                            // 2) Block anything below 1 (including negatives, zero, non-numeric)
                                                            if (!Number.isInteger(num) || num < 1) {
                                                                return;
                                                            }

                                                            // 3) Finally, accept it
                                                            handleChange(e);
                                                            setFieldValue("persons", raw);
                                                            // **clear the old table** any time they pick a new party size
                                                            if (!dialogPrefill?.table) {
                                                                setFieldValue("table", "");
                                                                setFieldError("table", undefined);
                                                            }
                                                            // setFieldValue("table", "");
                                                            // setFieldError("table", undefined);
                                                        }}
                                                        onBlur={handleBlur}
                                                        error={touched.persons && Boolean(errors.persons)}
                                                        helperText={touched.persons && errors.persons}
                                                        placeholder="1"
                                                        inputProps={{
                                                            min: 1,
                                                        }}
                                                        sx={{
                                                            // hide spinner in Chrome/Safari
                                                            "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                                                            {
                                                                WebkitAppearance: "none",
                                                                margin: 0,
                                                            },
                                                            // hide spinner in Firefox
                                                            "input[type=number]": {
                                                                MozAppearance: "textfield",
                                                            },
                                                            width: "130px",
                                                        }}
                                                    />
                                                </Grid>

                                                {/* Table Selection */}
                                                <Grid item xs={6.9}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <CustomTypography
                                                            label={"Select Table"}
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600, fontSize: "14px",
                                                                color: theme.palette.typographyLabel.main,
                                                                mb: 0
                                                            }}
                                                        />
                                                        <Tooltip title="Add Table" arrow placement="top">
                                                            <IconButton
                                                                size="small"
                                                                // onClick={openStationModal}
                                                                onClick={() => onAddTable && onAddTable(getCurrentDraft(values))}
                                                                sx={{
                                                                    backgroundColor: theme.palette.primary.main,
                                                                    color: theme.palette.common.white,
                                                                    '&:hover': {
                                                                        backgroundColor: theme.palette.primary.dark,
                                                                    },
                                                                    width: 20,
                                                                    height: 20,
                                                                    ml: 1
                                                                }}
                                                            >
                                                                <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <CustomSelect
                                                        name="table"
                                                        key={tableOptions.length}   // 🔑 force re-render when tables update
                                                        value={values?.table}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            const tableId = e.target.value;
                                                            // Don't validate immediately
                                                            setFieldValue("table", e.target.value);
                                                            // if (dialogType === DIALOG_TYPES.TABLE_BOOKING &&
                                                            //     values.day && values.time_slot && tableId) {
                                                            //          const { utcDate, utcTime } = getUTCDateTime(
                                                            //                 values?.day,
                                                            //                 values.time
                                                            //               );
                                                            //              let booking_date = utcDate;
                                                            //              let booking_time = utcTime;
                                                            //     onBookingParamsChange({
                                                            //         booking_date: booking_date,
                                                            //         booking_time: booking_time,
                                                            //         table_id:     tableId
                                                            //     });
                                                            // }
                                                        }}
                                                        onBlur={handleBlur}
                                                        error={touched.table && errors.table}
                                                        helperText={touched.table && errors.table}
                                                        placeholder={
                                                            isLoadingTables ? "Loading tables..." : "Select"
                                                        }
                                                        options={tableOptions}
                                                        disabled={isLoadingTables || Boolean(dialogPrefill?.table)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    {/* Additional fields for Table Booking */}
                                    {dialogType === DIALOG_TYPES.TABLE_BOOKING && (
                                        <TableBookingFields
                                            categories={categories}
                                            loadingSlots={loadingSlots}
                                            slotsError={slotsError}
                                            onAddTimeSlot={(slotValues) => onAddTimeSlot && onAddTimeSlot(getCurrentDraft(slotValues || values))}
                                        />

                                        // <>
                                        //     {/* Day Selection */}
                                        //     <Box sx={{ mb: '20px' }}>
                                        //         <CustomTypography
                                        //             label={"Select Day"}
                                        //             variant="body2"
                                        //             sx={{
                                        //                 mb: 0.5, fontWeight: 600, fontSize: "14px",
                                        //                 color: theme.palette.typographyLabel.main
                                        //             }}
                                        //         />
                                        //         <ToggleButtonGroup
                                        //             value={values.day}
                                        //             exclusive
                                        //             onChange={(e, newDay) => {
                                        //                 if (newDay) setFieldValue("day", newDay);
                                        //             }}
                                        //             aria-label="select day"
                                        //             sx={{
                                        //                 display: "flex",
                                        //                 flexWrap: "wrap",
                                        //                 "& .MuiToggleButtonGroup-grouped": {
                                        //                     border: "1px solid rgba(161, 161, 161, 1)",
                                        //                     borderRadius: "10px !important",
                                        //                     m: 0.5,
                                        //                     height: "52px",
                                        //                     maxWidth: "100px",
                                        //                     flex: "1 0 auto",
                                        //                     fontSize: "16px",
                                        //                     padding: "9px",
                                        //                 },
                                        //             }}
                                        //         >
                                        //             {days?.map((day, index) => (
                                        //                 <ToggleButton
                                        //                     key={day.value}
                                        //                     value={day.value}
                                        //                     aria-label={day.label}
                                        //                     disableRipple
                                        //                     disableFocusRipple
                                        //                     sx={{
                                        //                         flexDirection: "column", p: 1, width: "100px", borderRadius: "10px !important", textTransform: "none",
                                        //                         backgroundColor: values.day === day.value ? theme.palette.primary.main : "transparent",
                                        //                         color: values.day === day.value ? theme.palette.secondary.main : theme.palette.drawermenuitem.main,
                                        //                         "&.Mui-selected": {
                                        //                             backgroundColor: theme.palette.primary.main,
                                        //                             color: theme.palette.secondary.main,
                                        //                         },
                                        //                         "&.Mui-selected:hover": {
                                        //                             backgroundColor: theme.palette.primary.main,
                                        //                             color: theme.palette.secondary.main,
                                        //                         },
                                        //                     }}
                                        //                 >
                                        //                     <CustomTypography
                                        //                         label={day.label}
                                        //                         sx={{fontSize: "16px", fontWeight: 700, lineHeight: '19px', mb:0}}
                                        //                     />
                                        //                     <CustomTypography
                                        //                         label={day.subLabel}
                                        //                         sx={{ fontSize: "16px", lineHeight: '18px',mb:0 }}
                                        //                     />
                                        //                 </ToggleButton>
                                        //             ))}
                                        //         </ToggleButtonGroup>
                                        //     </Box>

                                        //     {/* Time Slot Selection */}
                                        //     <Box sx={{ mb: '15px' }}>
                                        //         <CustomTypography
                                        //             label={"Select Time Slot"}
                                        //             variant="body2"
                                        //             sx={{
                                        //                 mb: 0.5, fontWeight: 600, fontSize: "14px",
                                        //                 color: theme.palette.typographyLabel.main
                                        //             }}
                                        //         />
                                        //         <ToggleButtonGroup
                                        //             value={values.time_slot}
                                        //             exclusive
                                        //             onChange={(e, newTimeSlot) => {
                                        //                 if (newTimeSlot) {
                                        //                     setFieldValue("time_slot", newTimeSlot);
                                        //                     setSelectedTimeSlot(newTimeSlot);
                                        //                     setFieldValue("time", ""); // Reset time when changing time slot
                                        //                 }
                                        //             }}
                                        //             aria-label="select time slot"
                                        //             sx={{
                                        //                 display: "flex",
                                        //                 "& .MuiToggleButtonGroup-grouped": {
                                        //                     // border: "1px solid rgba(0, 0, 0, 0.12)",
                                        //                     border: "1px solid rgba(161, 161, 161, 1)",
                                        //                     borderRadius: "10px !important",
                                        //                     m: 0.5,
                                        //                     padding: "4px 8px 4px 8px",
                                        //                     "&:hover": {
                                        //                         backgroundColor: "transparent !important",
                                        //                         color: "inherit !important",
                                        //                     },
                                        //                 },
                                        //             }}
                                        //         >
                                        //             {Object.keys(timeSlots).map((slot) => (
                                        //                 <ToggleButton
                                        //                     key={slot}
                                        //                     value={slot}
                                        //                     aria-label={slot}
                                        //                     sx={{
                                        //                         textTransform: "none",
                                        //                         fontWeight: 'normal',
                                        //                         backgroundColor: values.time_slot === slot ? theme.palette.primary.main : "transparent",
                                        //                         color: values.time_slot === slot ? theme.palette.secondary.main : "inherit",
                                        //                         paddingTop: '5px',
                                        //                         "&.Mui-selected": {
                                        //                             backgroundColor: theme.palette.primary.main,
                                        //                             color: theme.palette.secondary.main,
                                        //                         },
                                        //                         "&.Mui-selected:hover": {
                                        //                             backgroundColor: `${theme.palette.primary.main} !important`,
                                        //                             color: `${theme.palette.secondary.main} !important`,
                                        //                         },
                                        //                     }}
                                        //                 >
                                        //                     {slot}
                                        //                 </ToggleButton>
                                        //             ))}
                                        //         </ToggleButtonGroup>
                                        //     </Box>

                                        //     {/* Time Selection */}
                                        //     {values.time_slot && (
                                        //         <Box sx={{ mb: '20px' }}>
                                        //             <ToggleButtonGroup
                                        //                 value={values.time}
                                        //                 exclusive
                                        //                 onChange={(e, newTime) => {
                                        //                     if (newTime) setFieldValue("time", newTime);
                                        //                 }}
                                        //                 aria-label="select time"
                                        //                 sx={{
                                        //                     display: "flex",
                                        //                     flexWrap: "wrap",
                                        //                     backgroundColor: "#F5F5F5",
                                        //                     borderRadius: "10px",
                                        //                     padding: "10px",
                                        //                     "& .MuiToggleButtonGroup-grouped": {
                                        //                         border: "1px solid rgba(0, 0, 0, 0.12)",
                                        //                         borderRadius: "10px !important",
                                        //                         m: 0.5,
                                        //                         "&:hover": {
                                        //                             backgroundColor: "transparent !important",
                                        //                             color: "inherit !important",
                                        //                         },
                                        //                     },
                                        //                 }}
                                        //             >
                                        //                 {timeSlots[values.time_slot]?.map((time) => (
                                        //                     <ToggleButton
                                        //                         key={time}
                                        //                         value={time}
                                        //                         aria-label={time}
                                        //                         sx={{
                                        //                             textTransform: "none",
                                        //                             borderRadius: "20px",
                                        //                             border: '1px solid #4D4D4D !important',
                                        //                             padding: "2px 8px 2px 8px",
                                        //                             fontWeight: 'normal',
                                        //                             paddingTop: '4px',
                                        //                             backgroundColor: values.time === time ? "white" : "white",
                                        //                             color: values.time === time ? "#fff" : "inherit",
                                        //                             "&.Mui-selected": {
                                        //                                 backgroundColor: theme.palette.primary.main,
                                        //                                 color: theme.palette.secondary.main,
                                        //                             },
                                        //                             "&.Mui-selected:hover": {
                                        //                                 backgroundColor: `${theme.palette.primary.main} !important`,
                                        //                                 color: `${theme.palette.secondary.main} !important`,
                                        //                             },
                                        //                         }}
                                        //                     >
                                        //                         {time}
                                        //                     </ToggleButton>
                                        //                 ))}
                                        //             </ToggleButtonGroup>
                                        //             {touched.time && errors.time && (
                                        //                 <Typography
                                        //                     variant="body2"
                                        //                     color="error"
                                        //                     sx={{ marginTop: "4px", fontSize: "12px" }}
                                        //                 >
                                        //                     {errors.time}
                                        //                 </Typography>
                                        //             )}
                                        //         </Box>
                                        //     )}
                                        // </>
                                    )}

                                    {dialogType === DIALOG_TYPES.PHONE_ORDER && (
                                        <>
                                            <CustomTypography
                                                label={"Customer Address"}
                                                variant="body2"
                                                sx={{
                                                    mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                    color: theme.palette.typographyLabel.main
                                                }}
                                            />
                                            <Autocomplete
                                                id="phone-order-address-autocomplete"
                                                options={placesSuggestions.map((suggestion) => ({ description: suggestion.description, place_id: suggestion.place_id }))}
                                                disabled={!ready}
                                                value={customerAddress || ""}
                                                inputValue={customerAddress || ""}
                                                filterOptions={(x) => x}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') { return option; }
                                                    return option.description || '';
                                                }}
                                                onInputChange={(e, val, reason) => {
                                                    setDeliveryFeeErrorMessage("");
                                                    setPlaceValue(val);
                                                    setCustomerAddress(val);
                                                    setFieldValue("customer_address", val);

                                                    // Address is changed/cleared, so previous delivery fee is no longer valid


                                                    if (reason === "clear" || !val || val.trim() === "") {
                                                        setCustomerAddress("");
                                                        setSelectedGoogleAddress(null);
                                                        setCustomerLat("");
                                                        setCustomerLong("");
                                                        setAddressDetails(null);
                                                        setDeliveryFeeApiError(false);
                                                        setDeliveryFeeErrorMessage("");
                                                        setCustomerDeliveryFee(0);

                                                        setFieldValue("customer_address", "");
                                                        setFieldValue("lat", "");
                                                        setFieldValue("long", "");
                                                        setFieldValue("delivery_fee", 0);

                                                        // Important: mark address as touched so Yup validation runs
                                                        setFieldTouched("customer_address", true);
                                                    }
                                                    // Important:
                                                    // If user types something different from the selected Google address,
                                                    // keep the previous Google address as the valid address.
                                                    if (selectedGoogleAddress && val !== selectedGoogleAddress.full_address) {
                                                        console.log("User edited address. Keeping last Google selected address:", selectedGoogleAddress);
                                                    }
                                                }}
                                                onChange={(event, newValue) => {
                                                    handleSelectLocation(newValue, setFieldValue, setFieldError);
                                                }}
                                                onBlur={() => {
                                                    setFieldTouched("customer_address", true);
                                                    clearSuggestions();
                                                }}
                                                freeSolo
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        name="customer_address"
                                                        error={Boolean((touched.customer_address && errors.customer_address) || deliveryFeeErrorMessage)}
                                                        helperText={deliveryFeeErrorMessage || (touched.customer_address ? errors.customer_address : "")}
                                                        placeholder="Search Customer Address / Google Places"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{
                                                            width: "400px",
                                                            "& .MuiOutlinedInput-root": {
                                                                height: 45,
                                                                minHeight: 45,
                                                                borderRadius: "8px",
                                                                fontSize: "14px",
                                                                "& .MuiOutlinedInput-input": {
                                                                    padding: "10px 2px",
                                                                },
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Box sx={{ mt: 2 }}>
                                                <CustomTypography
                                                    label={"Building Number"}
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                        color: theme.palette.typographyLabel.main
                                                    }}
                                                />
                                                <CustomTextField
                                                    name="building_number"
                                                    value={values.building_number || ""}
                                                    sx={{
                                                        width: "400px",
                                                        height: '45px'
                                                    }}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Building / Flat / Unit Number"
                                                />
                                            </Box>

                                            <Box sx={{ mt: 2 }}>
                                                <CustomTypography
                                                    label={"Building Name"}
                                                    variant="body2"
                                                    sx={{
                                                        mb: 0.5, fontWeight: 600, fontSize: "14px",
                                                        color: theme.palette.typographyLabel.main
                                                    }}
                                                />
                                                <CustomTextField
                                                    name="building_name"
                                                    value={values.building_name || ""}
                                                    sx={{
                                                        width: "400px",
                                                        height: '45px'
                                                    }}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Bulding Name"
                                                />
                                            </Box>
                                        </>
                                    )}

                                    {/* Continue Button */}
                                    <Box className='continue-btn-form' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {(() => {
                                            const rawPhone = values?.phone_number ?? phoneNumber ?? "";
                                            const effectiveDigits = String(rawPhone).replace(/\D/g, "");
                                            const isPhoneValid = effectiveDigits.length >= 10;
                                            const rawName = values?.customer_name ?? customerName ?? "";
                                            const isNameValid = Boolean(String(rawName).trim());

                                            let isFormLogicallyValid = true;
                                            if (dialogType === DIALOG_TYPES.TAKE_AWAY) {
                                                isFormLogicallyValid = isPhoneValid && isNameValid;
                                            } else if (dialogType === DIALOG_TYPES.DINE_IN) {
                                                const hasTable = Boolean(values?.table);
                                                const hasPersons = Boolean(values?.persons && Number(values?.persons) >= 1);
                                                if (!mandatoryCustomerDetailsDineIn && !showCustomerFields) {
                                                    isFormLogicallyValid = hasTable && hasPersons;
                                                } else {
                                                    isFormLogicallyValid = hasTable && hasPersons && isPhoneValid && isNameValid;
                                                }
                                            } else if (dialogType === DIALOG_TYPES.TABLE_BOOKING) {
                                                const hasTable = Boolean(values?.table);
                                                const hasPersons = Boolean(values?.persons && Number(values?.persons) >= 1);
                                                const hasSlot = Boolean(values?.day && values?.time_slot && values?.time);
                                                isFormLogicallyValid = hasTable && hasPersons && hasSlot && isPhoneValid && isNameValid;
                                            } else if (dialogType === DIALOG_TYPES.PHONE_ORDER) {
                                                isFormLogicallyValid = isPhoneValid && isNameValid && isPhoneOrderReady;
                                            }

                                            const hasBlockingError = Boolean(
                                                errors?.persons ||
                                                errors?.table ||
                                                errors?.time ||
                                                errors?.day ||
                                                errors?.time_slot ||
                                                errors?.customer_address ||
                                                deliveryFeeErrorMessage
                                            );

                                            const isContinueDisabled =
                                                !isFormLogicallyValid ||
                                                hasBlockingError ||
                                                isSubmitting ||
                                                isAddingCustomer ||
                                                !isPhoneOrderReady;

                                            return (
                                                <CustomButton
                                                    type="submit"
                                                    fullWidth
                                                    loading={isSubmitting || isAddingCustomer}
                                                    disabled={isContinueDisabled}
                                                    sx={{ borderRadius: "10px", width: "296px", py: 3.1 }}
                                                    onClick={() => { validateForm().then((errors) => { console.log(errors, "Form Submit Error"); }); }}
                                                >
                                                    Continue
                                                </CustomButton>
                                            );
                                        })()}

                                        {isExpressDineIn && !showCustomerFields && (
                                            <Box
                                                onClick={() => setShowCustomerFields(true)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: "296px",
                                                    mt: 1.5,
                                                    p: '10px 14px',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: '#ffffff',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        borderColor: '#B0B0B0',
                                                        backgroundColor: 'rgba(0,0,0,0.02)',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <PersonAddOutlinedIcon sx={{ fontSize: 22, color: '#111111' }} />
                                                    <Box sx={{ textAlign: 'left' }}>
                                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111111', lineHeight: 1.2 }}>
                                                            Enter customer details
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 400, color: '#757575', lineHeight: 1.3, mt: 0.3 }}>
                                                            Phone number and name are optional
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Form>
                        )
                    }}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default OrderDialog;
